/**
 * Handwriting fonts for the "Type" signature option — the signer's full name
 * rendered in one of 10 script faces, rasterized to a transparent PNG so the
 * stamped result is pixel-identical on every device (no font dependency at
 * render time). Fonts are self-hosted via @fontsource (bundled, no CDN).
 */
import '@fontsource/dancing-script/700.css';
import '@fontsource/great-vibes/400.css';
import '@fontsource/pacifico/400.css';
import '@fontsource/caveat/700.css';
import '@fontsource/satisfy/400.css';
import '@fontsource/sacramento/400.css';
import '@fontsource/allura/400.css';
import '@fontsource/parisienne/400.css';
import '@fontsource/kalam/700.css';
import '@fontsource/homemade-apple/400.css';

export interface SignatureFont {
  /** CSS font-family. */
  family: string;
  /** font-weight the @fontsource face was imported at. */
  weight: number;
}

export const SIGNATURE_FONTS: SignatureFont[] = [
  { family: 'Dancing Script', weight: 700 },
  { family: 'Great Vibes', weight: 400 },
  { family: 'Pacifico', weight: 400 },
  { family: 'Caveat', weight: 700 },
  { family: 'Satisfy', weight: 400 },
  { family: 'Sacramento', weight: 400 },
  { family: 'Allura', weight: 400 },
  { family: 'Parisienne', weight: 400 },
  { family: 'Kalam', weight: 700 },
  { family: 'Homemade Apple', weight: 400 },
];

/**
 * Signature ink and paper. The drawn/typed signature is a PNG that lands on the
 * PDF, so it always renders dark ink on white paper, in both themes. Previews of
 * signatures, initials and stamps sit on this paper too (a transparent PNG with
 * dark ink would vanish on a dark surface). Deliberate exception to the tokens.
 */
export const SIGNATURE_INK = '#111827';
export const SIGNATURE_PAPER = '#ffffff';
export const paperStyle = { backgroundColor: SIGNATURE_PAPER, color: SIGNATURE_INK } as const;

/** Render `text` in the given font to a transparent PNG data URL (black ink,
 *  high-DPI, trimmed to fit). Waits for the webfont to load first. */
export async function renderTypedSignature(text: string, font: SignatureFont): Promise<string> {
  const value = text.trim() || ' ';
  const fontPx = 64;
  const spec = `${font.weight} ${fontPx}px "${font.family}"`;
  try {
    await document.fonts.load(spec, value);
  } catch {
    /* fall through — canvas will use whatever is available */
  }
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = spec;
  const w = Math.ceil(measure.measureText(value).width) + 40;
  const h = Math.ceil(fontPx * 1.6);
  const dpr = window.devicePixelRatio || 2;
  const canvas = document.createElement('canvas');
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  ctx.font = spec;
  ctx.fillStyle = SIGNATURE_INK;
  ctx.textBaseline = 'middle';
  ctx.fillText(value, 20, h / 2);
  return canvas.toDataURL('image/png');
}
